import { Box, Button, Typography } from '@mui/material';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * GP-9: This is an optional step in the Calc Job Dialog Wizard for policies with aggregates.
 * The user may select overrides for the calculation
 */
export const OvrdSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep }) => {

  const rsrc = useRsrc();

  const [currJobIndex, setCurrJobIndex] = useState(0);

  const [subtitle, setSubtitle] = useState();

  const [isDerived, setIsDerived] = useState();
  const [isRealloc, setIsRealloc] = useState();

  const jobReqsList = calcChainReq.calcJobReqs;
  const liabPolType = calcChainReq.liabPolType;

  const currJobReq = useMemo(() => jobReqsList[currJobIndex], [jobReqsList, currJobIndex]);

  useEffect(() => {
    // Pol Types
    const derRowsReq = {
      enumCode: 'POL_TYPE',
      filterMethod: 'derivedPolTypes'
    }
    rsrc.data('gp', 'ENUM_LIST_DATA_PLUGIN', derRowsReq).then((derivedPols => {
      const polCodes = derivedPols.map(pol => pol.code);
      const derived = polCodes.includes(liabPolType.code);
      setIsDerived(derived);

      if (!derived) {
        const reallocRowsReq = {
          enumCode: 'POL_TYPE',
          filterMethod: 'reallocPolTypes'
        }

        rsrc.data('gp', 'ENUM_LIST_DATA_PLUGIN', reallocRowsReq).then((reallocPols => {
          const polCodes = reallocPols.map(pol => pol.code);
          const realloc = polCodes.includes(liabPolType.code);
          setIsRealloc(realloc);
        }));
      }
    }));
  }, []);


  // Let parent know what the next page is
  useEffect(() => {
    if (isDerived) {
      setNextStep('Dependencies');
    } else if (isRealloc) {
      setNextStep('Reallocations');
    } else {
      setNextStep('Options');
    }
  }, [isDerived, isRealloc])


  const entityListRef = useRef();
  const clientSideRowData = useCallback((setDataSource) => {
    const currPeriod = currJobReq?.period.code;

    const rowsReq = {
      replaceParams: {
        polType: liabPolType.code,
        period: currPeriod
      }
    };
    rsrc.rows('gp', 'CALC_TRX_OVRD_CALCS_SEL', rowsReq).then((rows) => {
      setDataSource(rows);

      const selectedCalcOvrds = currJobReq.calcTrxOvrdCalcIds;
      if (!selectedCalcOvrds) return;

      const gridApi = entityListRef.current?.gridRef.current?.gridApiRef.current;
      gridApi.forEachNode((node) => {
        if (selectedCalcOvrds.includes(node.data?.id)) {
          node.setSelected(true);
        }
      })
    });

  }, [currJobIndex]);


  const augFieldDefs = (fieldToFieldDef) => {
    const columns = ['name', 'polType', 'periodId', 'recBy', 'status', 'startInstant'];

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      }
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;
  };

  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedOvrdIds = selectedRows.map(row => row.id);

    setCalcChainReq(prev => {
      const updated = [...prev.calcJobReqs];
      updated[currJobIndex] = {
        ...updated[currJobIndex],
        calcTrxOvrdCalcIds: selectedOvrdIds
      };

      return { ...prev, calcJobReqs: updated };
    });
  };

  useEffect(() => {
    const nCalcs = jobReqsList.length;
    if (nCalcs === 1) {
      setSubtitle(`Select Calculations from which Transaction Overrides should be reused.`);
    } else {
      const currJobReq = jobReqsList?.[currJobIndex];
      const currPeriod = currJobReq?.period.code;
      setSubtitle(`Select Calculations from which Transaction Overrides should be reused for the ${currPeriod} calculation (${currJobIndex + 1} of ${nCalcs}).`);
    }
  }, [currJobIndex]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1
        }}>
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {jobReqsList.length > 1 &&
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex - 1)}
                disabled={currJobIndex === 0}
                sx={{ whiteSpace: 'nowrap' }}>
                Previous Calculation
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex + 1)}
                disabled={currJobIndex === jobReqsList.length - 1}
                sx={{ whiteSpace: 'nowrap' }}>
                Next Calculation
              </Button>
            </Box>}
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto', width: '100%' }}>
        <EntityList
          key={`${currJobIndex}`}
          ref={entityListRef}
          entity="CALC"
          clientSideRowData={clientSideRowData}
          augFieldDefs={augFieldDefs}
          events={{ onRowSelected: rowSelected }}
          gridOptions={{
            rowSelection: 'multiple',
          }}
        />
      </Box>
    </Box >
  );
};
